/**
 * Smart Flow's data source: the live options tape.
 *
 * It previously read the flow engine's `enriched_tape` — a worker-produced snapshot capped at a
 * thousand rows, which went stale whenever the engine was not running (its rows were two days old
 * while the tape was current) and whose "enrichment" columns were mostly zeros. The live tape is
 * 206M rows, current to the second, and filters entirely in QuestDB.
 *
 * The filters that matter are therefore pushed to the server — premium, DTE, moneyness, asset
 * class, cap tier, ticker — and the board renders the page that comes back. Sorting by premium
 * rather than time is deliberate: newest-first on this tape returns one saturating symbol inside a
 * single second, whereas premium-ranked gives a cross-section worth looking at.
 */

import { getApiUrl } from '../config/api';
import { getAuthToken } from './authUtils';
import {
  computeColor,
  computeFlowLabel,
  computeMoneyness,
  MoneynessBucket,
  ScannerTile,
  TileFlow,
  TileSide,
} from './rtlScannerTiles';
import { FlowFieldDef } from './rtlScannerFlowRows';
import { verdictFromAggregate } from './flowVerdictLabels';

/** Fields the live tape actually carries, for the custom filter builder and extra columns. */
export const LIVE_TAPE_FIELDS: FlowFieldDef[] = [
  { key: 'Ticker', label: 'Ticker', type: 'text' },
  { key: 'Contract', label: 'Contract', type: 'text' },
  { key: 'Option Type', label: 'Option type', type: 'enum', options: ['C', 'P'] },
  { key: 'Side', label: 'Tape side', type: 'enum', options: ['Ask', 'Bid', 'Mid'] },
  { key: 'Strike', label: 'Strike', type: 'number' },
  { key: 'Expiry Date', label: 'Expiry', type: 'text' },
  { key: 'DTE', label: 'DTE', type: 'number' },
  { key: 'Premium', label: 'Premium', type: 'number' },
  { key: 'Volume', label: 'Size', type: 'number' },
  { key: 'Last', label: 'Price', type: 'number' },
  { key: 'Spot', label: 'Spot', type: 'number' },
  { key: 'Stock', label: 'Underlying price', type: 'number' },
  { key: 'Condition', label: 'Condition', type: 'text' },
  { key: 'exchange', label: 'Exchange', type: 'number' },
  { key: 'sequence', label: 'Sequence', type: 'number' },
  { key: 'timestamp', label: 'Print time', type: 'text' },
];

export interface LiveTapeQuery {
  search?: string;
  fromTs?: string;
  toTs?: string;
  minPremium?: number;
  maxDte?: number;
  moneyness?: string;
  underlyingClass?: string;
  capTier?: string;
  sortBy?: string;
  limit?: number;
}

export interface LiveTapePage {
  tiles: ScannerTile[];
  /** True match count from QuestDB when the server reports it exact. */
  total: number;
  totalExact: boolean;
}

function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : null;
}

function str(v: unknown): string {
  return v == null ? '' : String(v);
}

/** `20260821` → `2026-08-21`; dashed values pass through. */
function normalizeExpiry(raw: unknown): string {
  const s = str(raw).trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  return s;
}

/** Ask = buyer lifted the offer, bid = seller hit it; mid stays undirected. */
function sideToFlow(side: unknown): TileFlow {
  const s = str(side).trim().toLowerCase();
  if (s === 'ask' || s === 'buy') return 'BUY';
  if (s === 'bid' || s === 'sell') return 'SELL';
  return 'UNK';
}

/** One tape print → one scanner tile. Null when the row lacks contract identity. */
export function tapePrintToTile(row: Record<string, unknown>, index: number): ScannerTile | null {
  const t = str(row.Ticker || row.Underlying).trim().toUpperCase();
  if (!t) return null;
  const sideRaw = str(row['Option Type']).trim().toUpperCase();
  if (sideRaw !== 'C' && sideRaw !== 'P') return null;
  const side = sideRaw as TileSide;
  const strike = num(row.Strike);
  if (strike == null) return null;
  const exp = normalizeExpiry(row['Expiry Date']);
  if (!exp) return null;

  const flow = sideToFlow(row.Side ?? row.Bid_Ask);
  // Underlying price is stamped at ingest now, so moneyness is computable on the tape.
  const spot = num(row.Stock) ?? num(row.Spot) ?? num(row['Underlying Price']);
  const { money, mc } = computeMoneyness(spot && spot > 0 ? spot : null, strike, side);

  return {
    t,
    rv: null, // the tape carries no relative volume
    side,
    strike,
    exp,
    dte: num(row.DTE),
    prem: num(row.Premium),
    flow,
    money,
    mc: mc as MoneynessBucket,
    fl: computeFlowLabel(flow, side),
    color: computeColor(flow, side),
    horizon_tml: null,
    id: `${str(row.sequence)}|${str(row.timestamp)}|${index}`,
    raw: row,
  };
}

function put(qs: URLSearchParams, key: string, value: string | number | undefined | null): void {
  if (value == null) return;
  const s = String(value).trim();
  if (!s || s === '0') return;
  qs.set(key, s);
}

export async function fetchLiveTapeTiles(
  q: LiveTapeQuery,
  signal?: AbortSignal,
): Promise<LiveTapePage> {
  const qs = new URLSearchParams({ data_source: 'questdb' });
  qs.set('limit', String(Math.max(1, Math.min(q.limit ?? 1000, 1000))));
  qs.set('offset', '0');
  // Premium-ranked, because newest-first returns one symbol inside a single second.
  qs.set('sort_by', q.sortBy || 'premium');
  qs.set('sort_dir', 'desc');
  put(qs, 'search', q.search?.toUpperCase());
  put(qs, 'min_premium', q.minPremium);
  put(qs, 'max_dte', q.maxDte);
  put(qs, 'moneyness', q.moneyness);
  put(qs, 'underlying_class', q.underlyingClass);
  put(qs, 'underlying_cap', q.capTier);
  put(qs, 'from_ts', q.fromTs);
  put(qs, 'to_ts', q.toTs);

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(getApiUrl(`/api/options-flow/flow?${qs.toString()}`), {
    headers,
    cache: 'no-store',
    signal,
  });
  if (!res.ok) throw new Error(`Live tape request failed (${res.status})`);
  const json = await res.json();
  if (!json?.success || !Array.isArray(json.data)) {
    throw new Error(typeof json?.message === 'string' ? json.message : 'Live tape returned no data');
  }

  // A tile is a contract, not a print: premium-ranked tape returns hundreds of prints of the
  // same strike, which would fill the board with one row repeated. Keep the largest print per
  // contract — the rows arrive premium-descending, so the first occurrence is the largest.
  const tiles: ScannerTile[] = [];
  const seen = new Set<string>();
  (json.data as Record<string, unknown>[]).forEach((row, i) => {
    const tile = tapePrintToTile(row, i);
    if (!tile) return;
    const key = str(row.Contract) || `${tile.t}|${tile.strike}|${tile.exp}|${tile.side}`;
    if (seen.has(key)) return;
    seen.add(key);
    tiles.push(tile);
  });

  return {
    tiles,
    total: Number(json.total) || tiles.length,
    totalExact: json.total_exact === true,
  };
}


// ── contract board ────────────────────────────────────────────────────────────

/**
 * OCC symbol → its parts. `SPXW  260828P07745000` is root, yymmdd, C/P, strike x1000.
 * Returns null rather than guessing when the string is not an OCC symbol.
 */
export function parseOccContract(
  occ: string,
): { ticker: string; expiry: string; side: TileSide; strike: number } | null {
  const raw = str(occ).trim().toUpperCase();
  const m = /^([A-Z.]{1,6})\s*(\d{6})([CP])(\d{8})$/.exec(raw.replace(/\s+/g, ' ').replace(/ /g, ''));
  if (!m) return null;
  const [, ticker, ymd, cp, strikeRaw] = m;
  const yy = Number(ymd.slice(0, 2));
  const year = 2000 + yy;
  const expiry = `${year}-${ymd.slice(2, 4)}-${ymd.slice(4, 6)}`;
  const strike = Number(strikeRaw) / 1000;
  if (!Number.isFinite(strike) || strike <= 0) return null;
  return { ticker, expiry, side: cp as TileSide, strike };
}

export interface ContractBoardRow {
  bucket: string;
  prints: number;
  premium: number;
  contracts: number;
  /**
   * Direction and structure, aggregated in QuestDB over every matching print. Optional
   * because a backend older than this feature answers without them, and the board should
   * render "not available" rather than fail to load.
   */
  buy_premium?: number;
  sell_premium?: number;
  multi_leg_contracts?: number;
  single_leg_contracts?: number;
  /**
   * Open interest for the contract, attached server-side for the ranked rows only.
   * `flow_buffer` does not record it, so the backend fills it from a per-expiry snapshot
   * cache; an expiry the vendor has no figure for stays absent and the column reads
   * "Not Available" rather than comparing volume against a zero.
   */
  oi?: number;
}

/**
 * Top contracts by aggregate premium across every matching print — the whole window, not a page.
 * Ranking single prints puts hundreds of rows of one strike at the top; ranking contracts is what
 * "where did the money go" actually asks.
 */
export async function fetchContractBoard(
  q: LiveTapeQuery,
  limit = 120,
  signal?: AbortSignal,
): Promise<ScannerTile[]> {
  const qs = new URLSearchParams({ data_source: 'questdb', group_by: 'contract' });
  qs.set('limit', String(Math.max(1, Math.min(limit, 500))));
  put(qs, 'search', q.search?.toUpperCase());
  put(qs, 'min_premium', q.minPremium);
  put(qs, 'max_dte', q.maxDte);
  put(qs, 'moneyness', q.moneyness);
  put(qs, 'underlying_class', q.underlyingClass);
  put(qs, 'underlying_cap', q.capTier);
  put(qs, 'from_ts', q.fromTs);
  put(qs, 'to_ts', q.toTs);

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(getApiUrl(`/api/options-flow/aggregate?${qs.toString()}`), {
    headers,
    cache: 'no-store',
    signal,
  });
  if (!res.ok) throw new Error(`Contract board request failed (${res.status})`);
  const json = await res.json();
  const buckets = (json?.buckets ?? []) as ContractBoardRow[];

  const tiles: ScannerTile[] = [];
  buckets.forEach((b) => {
    const parsed = parseOccContract(b.bucket);
    if (!parsed) return;
    // Aggregated rows carry no spot, so moneyness is not computable here and says so rather
    // than being invented; the moneyness filter still applies to the underlying prints.
    tiles.push({
      t: parsed.ticker,
      rv: null,
      side: parsed.side,
      strike: parsed.strike,
      exp: parsed.expiry,
      dte: null,
      prem: b.premium,
      flow: 'UNK',
      money: '----',
      mc: 'NA',
      verdict: verdictFromAggregate(b),
      fl: 'Aggregate',
      color: 'unk',
      horizon_tml: null,
      // The contract alone, with no array index. The bucket is a GROUP BY key and already
      // unique; folding in the row's position made a tile's identity change whenever its rank
      // shifted between polls, so React tore down and rebuilt rows that had merely moved.
      id: b.bucket,
      raw: {
        Contract: b.bucket,
        Ticker: parsed.ticker,
        Strike: parsed.strike,
        'Expiry Date': parsed.expiry,
        'Option Type': parsed.side,
        Premium: b.premium,
        Volume: b.contracts,
        Prints: b.prints,
      },
    });
  });
  return tiles;
}
